/**
 * ONIN PTY LTD - Analytics & Conversion Tracking utility
 * Supports Google Analytics (GA4) with dynamic injection from runtime environment.
 * Auto-logs to console in development so you can test events before adding the GA ID.
 */

// Define safe global type definitions for Google Analytics gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const IS_DEV = (import.meta as any).env.DEV;
let activeGaId = "";

// Synchronously initialize the Google Analytics native command queue.
// This prevents race conditions, ensuring page views or conversion events
// triggered during load are queued and automatically processed once GA4 is ready.
if (typeof window !== "undefined") {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
}

/**
 * Initializes Google Analytics dynamically by fetching the server configuration
 */
export async function initAnalytics() {
  try {
    const response = await fetch("/api/config");
    if (!response.ok) {
      throw new Error(`Server returned status ${response.status}`);
    }
    
    const config = await response.json();
    const gaId = config.gaMeasurementId || "";

    if (!gaId) {
      if (IS_DEV) {
        console.log("📊 [Analytics] VITE_GA_MEASUREMENT_ID is not configured. Running in Development log-only mode.");
      }
      return;
    }

    activeGaId = gaId;

    // Avoid duplicate script injections
    if (document.getElementById("google-tag-manager-script")) {
      return;
    }

    // Create script tag for gtag.js
    const script = document.createElement("script");
    script.id = "google-tag-manager-script";
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    script.async = true;
    document.head.appendChild(script);

    // Run standard configurations
    window.gtag("js", new Date());
    window.gtag("config", gaId, {
      page_path: window.location.pathname,
      send_page_view: true,
    });

    if (IS_DEV) {
      console.log(`📊 [Analytics] Google Analytics 4 initialized successfully at runtime with ID: ${gaId}`);
    }
  } catch (error) {
    console.error("📊 [Analytics] Failed to initialize Google Analytics dynamically:", error);
  }
}

/**
 * Tracks a custom event
 * @param eventName The action or event name (e.g. 'form_submission', 'cta_click')
 * @param params Additional event parameters (e.g. { category: 'engagement', label: 'Contact Form' })
 */
export function trackEvent(eventName: string, params: Record<string, any> = {}) {
  // Always log to development console for transparency
  if (IS_DEV) {
    console.log(`📊 [Analytics] Event: "${eventName}"`, params);
  }

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    const finalParams = { ...params };
    if (activeGaId) {
      finalParams.send_to = activeGaId;
    }
    window.gtag("event", eventName, finalParams);
  }
}

/**
 * Tracks an outbound link click or general CTA click
 */
export function trackCtaClick(ctaName: string, destination?: string) {
  trackEvent("cta_click", {
    event_category: "Engagement",
    event_label: ctaName,
    destination: destination || "Internal Link",
  });
}

/**
 * Tracks form submission conversion
 */
export function trackFormSubmission(formType: string, details: Record<string, any> = {}) {
  trackEvent("form_submission", {
    event_category: "Conversion",
    event_label: formType === "contact" ? "Contact Inquiry Form" : `Form: ${formType}`,
    ...details,
  });
}
